import { PlusOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EmployeesService } from 'services/EmployeesService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { formatDate } from 'utils';
import { priceRender } from 'utils/table';
import { EmployeesForm } from './EmployeeForm';
import { EmployeesTable } from './EmployeesTable';
import {
  calculationTypeMap,
  citizenshipMap,
  disabilityMap,
  positionMap,
  workWeekMap,
} from './datasource';

const PAGE_TITLE = 'Сотрудники';

function Employees() {
  useDocumentTitle(PAGE_TITLE);
  const navigate = useNavigate();
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const tribe_id = useTribe();
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    date_in: null,
    f_salary: undefined,
    t_salary: undefined,
    status: null,
    iin: null,
    full_name: null,
    position: null,
  });

  const { client_id: selectedClient } = filters;

  useEffect(() => {
    fetchEmployees();
  }, [page, perPage, filters]);

  const selectedClientOrganization = useMemo(() => {
    if (clients?.length > 0 && selectedClient) {
      const currClient = clients.find((el) => el.id === selectedClient);

      if (currClient) {
        return currClient?.organization;
      }
    }
    return '';
  }, [clients, selectedClient]);

  const selectedClientHasSupervisor = useMemo(() => {
    if (employees?.length > 0 && selectedClient) {
      return employees.filter((el) => el?.position === 'supervisor').length > 0;
    }
    return false;
  }, [employees, selectedClient]);

  const recordFields = useMemo(() => {
    return [
      { label: 'Личные данные', key: 'sectionTitle' },
      {
        label: 'ФИО',
        key: 'full_name',
        computedValue: `${activeRecord?.last_name} ${
          activeRecord?.first_name
        } ${activeRecord?.patronymic || ''}`,
      },
      {
        label: 'ИИН',
        key: 'iin',
      },
      {
        label: 'Дата рождения',
        key: 'birthday',
        computedValue: activeRecord?.birthday
          ? formatDate(activeRecord?.birthday)
          : '---',
      },
      { label: 'Должность', key: 'sectionTitle' },
      {
        label: 'Тип',
        key: 'position',
        computedValue: positionMap[activeRecord?.position],
      },
      {
        label: 'Должность',
        key: 'custom_position',
      },
      { label: 'Зарплата', key: 'sectionTitle' },
      {
        label: 'Размер текущей заработной платы',
        key: 'current_salary',
        computedValue: `${priceRender(activeRecord?.current_salary)} ₸`,
      },
      {
        label: 'Размер начальной заработной платы',
        key: 'earn',
        computedValue: `${priceRender(activeRecord?.earn)} ₸`,
      },
      {
        label: 'Дата приема',
        key: 'date',
        computedValue: formatDate(activeRecord?.date),
      },
      {
        label: 'Вид расчета',
        key: 'calculation_type',
        computedValue: calculationTypeMap[activeRecord?.calculation_type],
      },
      {
        label: 'Рабочая неделя',
        key: 'work_week',
        computedValue: workWeekMap[activeRecord?.work_week],
      },
      { label: 'Дополнительно', key: 'sectionTitle' },
      {
        label: 'Гражданство',
        key: 'citizenship',
        computedValue: citizenshipMap[activeRecord?.citizenship],
      },
      {
        label: 'Инвалидность',
        key: 'disability',
        computedValue: disabilityMap[activeRecord?.disability],
      },
      {
        label: 'Вычет МЗП',
        key: 'tax_deduction',
        computedValue: activeRecord?.tax_deduction ? 'Да' : 'Нет',
      },
      {
        label: 'Дата выхода на пенсию',
        key: 'pensioner_date',
        computedValue: activeRecord?.pensioner
          ? formatDate(activeRecord?.pensioner_date)
          : 'Не пенсионер',
      },
    ];
  }, [activeRecord]);

  const fetchEmployees = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await EmployeesService.getEmployees(
        page,
        perPage,
        filters,
        tribe_id
      );

      setEmployees(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    closeForm();
    fetchEmployees();
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await EmployeesService.deleteEmployeeOfClient(
        activeRecord.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchEmployees();
        message.success('Сотрудник успешно удален');
      }
    } catch (err) {
      message.error('Не удалось удалить сотрудника');
    } finally {
      hide();
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const extraButton = (
    <Button
      onClick={() =>
        navigate(`/clients/employees/records?iin=${activeRecord?.iin}`)
      }
    >
      Кадровые записи сотрудника
    </Button>
  );

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClientsChange={(clients) => setClients(clients)}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="worker" action="create">
          <Button
            disabled={!selectedClient}
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateForm}
          >
            Создать
          </Button>
        </AccessControl>
      </ActionsWrapper>

      <EmployeesTable
        allowFilter
        items={employees}
        current={page}
        pageSize={perPage}
        loading={loading}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <RecordInfoDrawer
        drawerTitle="Информация о сотруднике"
        pageId="worker"
        record={activeRecord}
        recordFields={recordFields}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        extraActions={extraButton}
        deleteConfirmText="Вы точно хотите удалить этого сотрудника?"
      />

      <EmployeesForm
        isActive={formIsActive}
        initialValues={activeRecord}
        clientId={activeRecord?.client_id || selectedClient}
        onSuccess={handleConfirm}
        onClose={closeForm}
        isIP={selectedClientOrganization === 'ИП'}
        hasSupervisor={selectedClientHasSupervisor}
      />
    </>
  );
}

export { Employees };
